/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useEffect, useState } from "react";
import { View } from "react-native";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@material-ui/core";
import Toggle from "react-toggle";
import { gql, NetworkStatus } from "@apollo/client";
import { PropagateLoader } from "react-spinners";
import { ic_refresh } from "react-icons-kit/md/ic_refresh";

import "./custom-toggle-style.css";

import * as UpduoAnalytics from "config/UpduoAnalytics";

import {
  GetKudosQuery,
  useApproveKudosMutation,
  useGetKudosQuery,
  UserType,
} from "graphql/internal/generated";
import {
  Text,
  styles,
  KudosCategoryIconGroup,
  ProfileImage,
  UpduoButton,
  ButtonType,
  ButtonSize,
  VideoModalLauncherIcon,
} from "UpduoLibrary";
import {
  formatedReadableDateStringShort,
  getNodes,
  useOnScrollToBottom,
} from "config/Utilities";
import {
  skaleWhite,
  upduoDarkGray,
  upduoErrorBackground,
  upduoPrimaryBlue,
  upduoPrimaryDark,
} from "config/UpduoColors";
import { NodeType } from "config/UtilityTypes";
import { useSelector } from "react-redux";
import { RootState } from "store/types";

export default function KudosModerationRoute() {
  const selfObject = useSelector((state: RootState) => state.selfObject);
  const [excludeWithoutMessage, setExcludeWithoutMessage] = useState(true);

  const {
    data: kudosData,
    error: kudosError,
    networkStatus: kudosNetworkStatus,
    fetchMore: fetchMoreKudos,
    refetch: refetchKudos,
  } = useGetKudosQuery({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    variables: {
      first: 10,
      getKudosRequest: {
        includeUnapproved: true,
        excludeWithoutMessage,
      },
    },
    onError: (err) => {
      console.error("Graphql Error:", err);
      UpduoAnalytics.logEvent("app-fetchkudos-error", {
        errorName: err.name,
        errorMessage: err.message,
      });
    },
  });

  useEffect(() => {
    refetchKudos();
  }, [selfObject.organizationId]);

  const { reCheckPageScroll } = useOnScrollToBottom(() => {
    if (
      kudosNetworkStatus === NetworkStatus.ready &&
      kudosData?.kudos2?.pageInfo?.hasNextPage
    ) {
      fetchMoreKudos({
        variables: {
          cursor: kudosData.kudos2.pageInfo.endCursor,
        },
      }).catch((err) => {
        console.error(err);
      });
    }
  });

  useEffect(() => {
    if (kudosData) {
      reCheckPageScroll();
    }
  }, [kudosData]);

  const kudos = getNodes(kudosData?.kudos2);

  const loading =
    kudosNetworkStatus === NetworkStatus.refetch ||
    kudosNetworkStatus === NetworkStatus.fetchMore ||
    kudosNetworkStatus === NetworkStatus.loading;

  return (
    <View
      style={{
        justifyContent: "center",
        alignItems: "center",
        flexGrow: 1,
        flexDirection: "row",
      }}
    >
      <View
        style={{
          backgroundColor: skaleWhite,
          borderRadius: 12,
          padding: 12,
          margin: 20,
          maxWidth: 1200,
          flexGrow: 1,
        }}
      >
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Text
            isHeading={true}
            style={{
              fontSize: 24,
              color: upduoPrimaryDark,
              marginHorizontal: 12,
              marginTop: 12,
            }}
          >
            {"Kudos"}
          </Text>
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            <Text style={{ marginHorizontal: 8 }}>
              {"Include without messages"}
            </Text>
            <View style={{ marginRight: 12 }}>
              <Toggle
                checked={!excludeWithoutMessage}
                readOnly={true}
                onClick={() => setExcludeWithoutMessage((old) => !old)}
                size={12}
                disabled={loading}
                className={"upduo-red-gray-toggle"}
              />
            </View>
            <UpduoButton
              text={"Refresh"}
              icon={ic_refresh}
              onPress={() => refetchKudos()}
              type={ButtonType.SECONDARY}
              size={ButtonSize.SMALL}
              disabled={loading}
            />
          </View>
        </View>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Text style={styles.tableHeaderText}>{"Session"}</Text>
                </TableCell>
                <TableCell>
                  <Text style={styles.tableHeaderText}>{"Date"}</Text>
                </TableCell>
                <TableCell>
                  <Text style={styles.tableHeaderText}>{"From"}</Text>
                </TableCell>
                <TableCell>
                  <Text style={styles.tableHeaderText}>{"To"}</Text>
                </TableCell>
                <TableCell>
                  <Text style={styles.tableHeaderText}>{"Kudos"}</Text>
                </TableCell>
                <TableCell>
                  <Text style={styles.tableHeaderText}>{"Message"}</Text>
                </TableCell>
                <TableCell>
                  <Text style={styles.tableHeaderText}>{"Approved"}</Text>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {kudosNetworkStatus === NetworkStatus.refetch
                ? null
                : kudos.map((kudos) => (
                    <KudosRow key={kudos.id} kudos={kudos} />
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
        {kudos.length === 0 && kudosNetworkStatus !== NetworkStatus.loading ? (
          <Text
            style={{
              textAlign: "center",
              marginTop: 20,
              marginBottom: 10,
              color: upduoDarkGray,
            }}
          >
            {"No kudos to display right now"}
          </Text>
        ) : null}
        {loading || kudosError || kudosData?.kudos2.pageInfo.hasNextPage ? (
          <View
            style={{
              minHeight: 50,
            }}
          >
            {loading ? (
              <View
                style={{
                  flexGrow: 1,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <PropagateLoader color={upduoPrimaryBlue} />
              </View>
            ) : kudosError ? (
              <Text
                style={{ marginVertical: 12 }}
              >{`Error loading kudos: ${kudosError.message}`}</Text>
            ) : null}
          </View>
        ) : null}
      </View>
    </View>
  );
}

function KudosRow({ kudos }: { kudos: NodeType<GetKudosQuery, "kudos2"> }) {
  // treat un-moderated kudos as approved
  const approved = kudos.approved !== false;
  return (
    <TableRow
      style={{
        verticalAlign: "top",
        backgroundColor: approved ? undefined : upduoErrorBackground,
      }}
    >
      <TableCell>
        {kudos.session ? (
          <VideoModalLauncherIcon session={kudos.session} />
        ) : null}
      </TableCell>
      <TableCell>
        <View style={styles.tableCell40}>
          <Text
            style={
              {
                whiteSpace: "nowrap",
              } as any
            }
          >
            {formatedReadableDateStringShort(
              new Date(Date.parse(kudos.created))
            )}
          </Text>
        </View>
      </TableCell>
      <TableCell>
        <View style={styles.tableCell40}>
          <UserTableCell user={kudos.sender} />
        </View>
      </TableCell>
      <TableCell>
        <View style={styles.tableCell40}>
          <UserTableCell user={kudos.recipient} />
        </View>
      </TableCell>
      <TableCell>
        <View style={styles.tableCell40}>
          <KudosCategoryIconGroup kudo={kudos} />
        </View>
      </TableCell>
      <TableCell style={{ verticalAlign: "middle" }}>
        <Text>{kudos.message}</Text>
      </TableCell>
      <TableCell>
        <View style={styles.tableCell40}>
          <KudosApproveToggleSwitch kudos={kudos} />
        </View>
      </TableCell>
    </TableRow>
  );
}

function UserTableCell({
  user,
}: {
  user: Pick<UserType, "firstName" | "lastName" | "profileImgSmUrl">;
}) {
  return (
    <View style={{ flexDirection: "row", alignItems: "center" }}>
      <ProfileImage userObject={user} size={30} style={{ marginRight: 12 }} />
      <Text>{`${user.firstName} ${user.lastName}`}</Text>
    </View>
  );
}

function KudosApproveToggleSwitch({
  kudos,
}: {
  kudos: NodeType<GetKudosQuery, "kudos2">;
}) {
  // treat un-moderated kudos as approved
  const approved = kudos.approved !== false;

  // TODO add error reporting
  const [approveKudos, { loading }] = useApproveKudosMutation({
    variables: {
      approveKudosRequest: {
        approved: !approved,
        kudosId: kudos.id,
      },
    },
    update: (cache) => {
      cache.writeFragment({
        data: { ...kudos, approved: !approved },
        fragment: gql`
          fragment NewKudos on KudosType {
            id
            approved
          }
        `,
      });
    },
    onError: (err) => {
      UpduoAnalytics.logEvent("user-moderatekudos-error", {
        kudosId: kudos.id,
        approved: kudos.approved,
        error: { message: err.message, name: err.name },
      });
    },
    onCompleted: () => {
      UpduoAnalytics.logEvent("user-moderatekudos", {
        kudosId: kudos.id,
        approved_before: kudos.approved,
        approved_after: !approved,
      });
    },
  });

  const _handleClick = () => {
    if (!loading) {
      approveKudos();
    }
  };

  return (
    <View style={{ flexDirection: "row", alignItems: "center" }}>
      <Toggle
        checked={approved}
        readOnly={true}
        onClick={_handleClick}
        size={12}
        disabled={loading}
        className={"upduo-red-green-toggle"}
      />
      {loading ? (
        <CircularProgress
          color={"inherit"}
          size={"1.2em"}
          style={{ marginLeft: 8 }}
        />
      ) : null}
    </View>
  );
}
